/**
 * @license
 * Copyright Futuro Team. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NbAuthIllegalTokenError, NbAuthResult, NbAuthStrategy, NbAuthStrategyClass } from '@nebular/auth';
import {
  graphqlPasswordStrategyOptions,
  NbGraphqlPasswordAuthStrategyOptions,
} from './graphql-password-strategy-options';
import { Apollo } from 'apollo-angular';
import { loginMutation, logout, refreshMutation } from './login-mutation-gql';
import { NbToastrService } from '@nebular/theme';

/**
 * The most common authentication provider for email/password strategy, extended to use GraphQL.
 *
 * Strategy settings. Note, there is no need to copy over the whole object to change the settings you need.
 * Also, this.getOption call won't work outside of the default options declaration
 * (which is inside of the `NbGraphqlPasswordAuthStrategy` class), so you have to replace it with a custom helper function
 * if you need it.
 *
 */
@Injectable()
export class NbGraphqlPasswordAuthStrategy extends NbAuthStrategy {
  protected defaultOptions: NbGraphqlPasswordAuthStrategyOptions = graphqlPasswordStrategyOptions;

  public static setup(
    options: NbGraphqlPasswordAuthStrategyOptions,
  ): [NbAuthStrategyClass, NbGraphqlPasswordAuthStrategyOptions] {
    return [NbGraphqlPasswordAuthStrategy, options];
  }

  public constructor(protected apollo: Apollo, private router: Router, private toasterService: NbToastrService) {
    super();
  }

  public authenticate(data?: any): Observable<NbAuthResult> {
    const module = 'login';
    const requireValidToken = this.getOption(`${module}.requireValidToken`);

    const { email, password } = data;

    return this.apollo
      .mutate({
        mutation: loginMutation,
        variables: { input: { email, password } },
      })
      .pipe(
        map((res) => {
          if (this.getOption(`${module}.alwaysFail`)) {
            throw this.createFailResponse(data);
          }
          return res;
        }),
        map((res) => {
          const response = (res.data as any).login;

          const { userRole, accessToken, refreshToken } = response;
          const success = userRole === 'Admin' || userRole === 'Partner';
          const errors = success ? [] : [`${userRole} role is not permitted`];

          return new NbAuthResult(
            success,
            response,
            this.getOption(`${module}.redirect.success`),
            errors,
            this.getOption('messages.getter')(module, res, this.options),
            this.createToken({ accessToken, refreshToken }, requireValidToken),
          );
        }),
        catchError((res) => {
          return this.handleResponseError(res, module);
        }),
      );
  }

  public register(): Observable<NbAuthResult> {
    return this.throwNotImplementedResponse('register');
  }

  public requestPassword(): Observable<NbAuthResult> {
    throw this.throwNotImplementedResponse('requestPassword');
  }

  public resetPassword(): Observable<NbAuthResult> {
    throw this.throwNotImplementedResponse('resetPassword');
  }

  public logout(): Observable<NbAuthResult> {
    return this.apollo
      .mutate({
        mutation: logout,
      })
      .pipe<NbAuthResult>(
        map(() => {
          this.router.navigate(['/auth/login']);

          return new NbAuthResult(true);
        }),
      );
  }

  public refreshToken(data: any): Observable<NbAuthResult> {
    const module = 'refresh';

    const { refreshToken } = data.token;

    if (!refreshToken) {
      return this.handleResponseError(null, module);
    }

    return this.apollo
      .mutate({
        mutation: refreshMutation,
        variables: { refreshToken },
      })
      .pipe(
        map((res) => {
          const response = (res.data as any).refreshSession;

          const { userRole, accessToken, refreshToken } = response;
          const success = userRole === 'Admin' || userRole === 'Partner';
          const errors = success ? [] : [`${userRole} role is not permitted`];

          return new NbAuthResult(
            success,
            response,
            this.getOption(`${module}.redirect.success`),
            errors,
            'token refreshed',
            this.createToken({ accessToken, refreshToken }),
          );
        }),
        catchError((res) => {
          return this.handleResponseError(res, module);
        }),
      );
  }

  protected throwNotImplementedResponse(method: string): Observable<NbAuthResult> {
    this.toasterService.show(`(${method}) This feature is not available yet.`, 'Action declined.');

    return observableOf(
      new NbAuthResult(false, {}, this.getOption(`login.redirect.failure`), ['Something went wrong.']),
    );
  }

  protected handleResponseError(res: any, module: string): Observable<NbAuthResult> {
    let errors = [];
    if (res instanceof HttpErrorResponse) {
      errors = this.getOption('errors.getter')(module, res, this.options);
    } else if (res instanceof NbAuthIllegalTokenError) {
      errors.push(res.message);
    } else {
      // graphql errors handling
      try {
        if (res.graphQLErrors != null) {
          res.graphQLErrors.forEach((e) => {
            if (e.message != null) {
              errors.push(e.message);
            }
          });
        }
      } catch {
        errors.push('Something went wrong.');
      }
    }
    return observableOf(new NbAuthResult(false, res, this.getOption(`${module}.redirect.failure`), errors));
  }
}
